/**
 * Storage Helper com TTL e Versionamento
 * 
 * Gerencia localStorage com:
 * - ✅ Expiração automática (TTL)
 * - ✅ Versionamento para limpar dados antigos
 * - ✅ Fallback para formato antigo (compatibilidade)
 * - ✅ Limpeza automática de dados expirados
 */

// Versão atual do sistema (incrementar quando mudar estrutura de dados)
const STORAGE_VERSION = '1.0.0';

/**
 * Salvar item no localStorage com TTL opcional
 * 
 * @param {string} key - Chave do item
 * @param {any} value - Valor a ser armazenado
 * @param {number|null} ttl - Tempo de vida em milissegundos (null = sem expiração)
 */
export const setItem = (key, value, ttl = null) => {
  try {
    const item = {
      value,
      timestamp: Date.now(),
      ttl,
      version: STORAGE_VERSION
    };
    
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.warn(`[Storage] Erro ao salvar '${key}':`, error);
  }
};

/**
 * Obter item do localStorage com verificação de TTL e versão
 * 
 * @param {string} key - Chave do item
 * @returns {any|null} - Valor armazenado ou null se não existir/expirado
 */
export const getItem = (key) => {
  try {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) return null;

    // Tentar parsear como novo formato
    try {
      const item = JSON.parse(itemStr);
      
      // Se não tem timestamp, é formato antigo (compatibilidade)
      if (!item.timestamp) {
        return item; // Retornar valor direto
      }

      // Verificar versão (limpar se mudou)
      if (item.version !== STORAGE_VERSION) {
        console.log(`[Storage] Limpando '${key}' - versão desatualizada`);
        localStorage.removeItem(key);
        return null;
      }

      // Verificar TTL (se definido)
      if (item.ttl && Date.now() - item.timestamp > item.ttl) {
        console.log(`[Storage] Limpando '${key}' - expirado`);
        localStorage.removeItem(key);
        return null;
      }

      return item.value;
    } catch {
      // Se falhar no parse, retornar valor direto (formato antigo)
      return itemStr;
    }
  } catch (error) {
    console.warn(`[Storage] Erro ao ler '${key}':`, error);
    return null;
  }
};

/**
 * Remover item do localStorage
 * 
 * @param {string} key - Chave do item
 */
export const removeItem = (key) => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn(`[Storage] Erro ao remover '${key}':`, error);
  }
};

/**
 * Limpar todos os itens expirados do localStorage
 */
export const cleanExpired = () => {
  try {
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      try {
        const itemStr = localStorage.getItem(key);
        const item = JSON.parse(itemStr);

        // Só verificar itens com nosso formato
        if (item.timestamp) {
          // Remover se expirou
          if (item.ttl && Date.now() - item.timestamp > item.ttl) {
            keysToRemove.push(key);
          }
          // Remover se versão mudou
          else if (item.version !== STORAGE_VERSION) {
            keysToRemove.push(key);
          }
        }
      } catch {
        // Ignorar itens que não são JSON ou não tem nosso formato
      }
    }

    // Remover itens expirados/desatualizados
    keysToRemove.forEach(key => {
      console.log(`[Storage] Limpando '${key}' automaticamente`);
      localStorage.removeItem(key);
    });

    if (keysToRemove.length > 0) {
      console.log(`[Storage] ${keysToRemove.length} item(s) limpo(s)`);
    }
  } catch (error) {
    console.warn('[Storage] Erro ao limpar expirados:', error);
  }
};

/**
 * Limpar TODOS os itens do localStorage (usar com cuidado!)
 */
export const clearAll = () => {
  try {
    localStorage.clear();
    console.log('[Storage] Todos os itens foram removidos');
  } catch (error) {
    console.warn('[Storage] Erro ao limpar tudo:', error);
  }
};

/**
 * Obter estatísticas do localStorage
 * 
 * @returns {object} - Estatísticas de uso
 */
export const getStats = () => {
  try {
    let totalItems = 0;
    let totalSize = 0;
    let expiredItems = 0;
    let outdatedItems = 0;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      const itemStr = localStorage.getItem(key);
      totalItems++;
      totalSize += itemStr.length;

      try {
        const item = JSON.parse(itemStr);
        if (item.timestamp) {
          // Contar expirados
          if (item.ttl && Date.now() - item.timestamp > item.ttl) {
            expiredItems++;
          }
          // Contar desatualizados
          if (item.version !== STORAGE_VERSION) {
            outdatedItems++;
          }
        }
      } catch {
        // Ignorar
      }
    }

    return {
      totalItems,
      totalSize,
      expiredItems,
      outdatedItems,
      sizeInKB: (totalSize / 1024).toFixed(2),
      sizeInMB: (totalSize / 1024 / 1024).toFixed(2)
    };
  } catch (error) {
    console.warn('[Storage] Erro ao obter estatísticas:', error);
    return null;
  }
};

// Exportar objeto com todos os métodos
const storage = {
  setItem,
  getItem,
  removeItem,
  cleanExpired,
  clearAll,
  getStats,
  version: STORAGE_VERSION
};

export default storage;

